import { ACCESS_TYPE, URL_CONSTANTS } from '../_constants';
import { ServiceHelper } from './ServiceHelper';

interface UpdateDeviceServerParams {
    uid: string;
    notification?: boolean;
    description?: string;
    room_uid?: string;
    access_type?: ACCESS_TYPE;
    is_barrier?: boolean;
    remote?: boolean;
    restrict_exit?: boolean;
}

export const deviceService = {
    sendActionToDevice,
    sendNoStatusActionToDevice,

    updateBrain,
    updateDevice,

    actionRequest,
    actionConfirm,

    getDeviceInfo,
    getBrainInfo,

    getDeviceFirmwareVersions,
    updateDeviceFirmwareVersion,

    rebootDevice,
    minihubLogConfig,
    minihubLogFetch,
    minihubRestart,
    minihubLink,
    minihubStatusAsk,
    minihubStatusRead,
    minihubHubUid,
    minihubResync,
    minihubPairing,
    minihubFactoryReset
};

async function getDeviceInfo(uid: string) {
    return await ServiceHelper.getRequest(
        URL_CONSTANTS.DEVICE_INFO,
        { uid },
    );
}

async function getBrainInfo(uid) {
    return await ServiceHelper.getRequest(
        URL_CONSTANTS.BRAIN_INFO,
        { uid },
    );
}

async function sendActionToDevice(uid, action) {
    const bodyObject = {
        uid,
        action,
    };
    const response = await ServiceHelper.postRequest(URL_CONSTANTS.DEVICE_ACTION, bodyObject);
    return response.action_uid;
}

function sendNoStatusActionToDevice(uid, thermostat_enabled, thermostat_automatic, thermostat_mode, temperature_manual) {
    const bodyObject = {
        uid,
        thermostat_enabled,
        thermostat_automatic,
        thermostat_mode,
        temperature_manual,
    };
    return ServiceHelper.postRequest(URL_CONSTANTS.DEVICE_NOSTATUS_ACTION, bodyObject);
}

function updateDevice({
    uid,
    notification,
    description,
    room_uid,
    access_type,
    is_barrier,
    remote,
    restrict_exit
}: UpdateDeviceServerParams) {
    return ServiceHelper.postRequest(URL_CONSTANTS.DEVICE_UPDATE, {
        uid,
        notification,
        desc: description,
        room_uid,
        access_type,
        is_barrier,
        remote,
        restrict_exit
    });
}

function updateBrain({
    uid,
    notification,
    description,
    room_uid,
}: UpdateDeviceServerParams) {
    return ServiceHelper.postRequest(URL_CONSTANTS.BRAIN_UPDATE, {
        uid,
        notification,
        desc: description,
        room_uid,
    });
}

function getDeviceFirmwareVersions({ uid }) {
    return ServiceHelper.getRequest(URL_CONSTANTS.DEVICE_UPDATE_LIST, { uid });
}

// `device` says which half the version is for: 1 the ESP32, 2 the EFR32, 3
// both (the firmware's own numbering). Omitted, the server updates both, which
// is what every caller wanted until the support panel needed to update just
// the BLE side (BAC-611).
function updateDeviceFirmwareVersion(uid, version, device: number | undefined = undefined) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.DEVICE_UPDATE_FIRMWARE,
        {
            uid,
            version,
            ...(device === undefined ? {} : { device }),
        },
        false
    );
}

// MiniHUB support orders (BAC-610). Superuser only on the server; the UI hides
// them for everyone else, but the guard that counts is the one in django.
function minihubLogConfig(uid, config) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_LOG_CONFIG,
        {
            uid,
            ...config,
        },
        false
    );
}

function minihubLogFetch(uid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_LOG_FETCH,
        {
            uid,
        },
        false
    );
}

function minihubLink(uid, mode) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_LINK,
        {
            uid,
            mode,
        },
        false
    );
}

// Asking and reading are two calls: the device answers over MQTT, not on this
// request (BAC-611).
function minihubStatusAsk(uid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_STATUS,
        {
            uid,
        },
        false
    );
}

function minihubStatusRead(uid) {
    return ServiceHelper.getRequest(URL_CONSTANTS.BRAIN_MINIHUB_STATUS, { uid });
}

// The uid the device logs are stamped with (BAC-733). The panel is drawn on
// the Ble's uid, but the gateway signs the logs with the hub's MQTT username,
// and only the server knows which hub sits behind a Ble. It rides on the
// status read; it is its own function so that reading it when the panel opens
// does not count as a poll of the status.
async function minihubHubUid(uid) {
    const answer: any = await minihubStatusRead(uid);
    return answer?.brain_uid ?? null;
}

function minihubResync(uid, list) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_RESYNC,
        {
            uid,
            list,
        },
        false
    );
}

function minihubPairing(uid, open) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_PAIRING,
        {
            uid,
            open,
        },
        false
    );
}

function minihubFactoryReset(uid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_FACTORY_RESET,
        {
            uid,
        },
        false
    );
}

function minihubRestart(uid, device) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.BRAIN_MINIHUB_RESTART,
        {
            uid,
            device,
        },
        false
    );
}

function rebootDevice(uid) {
    return ServiceHelper.postRequest(
        URL_CONSTANTS.DEVICE_REBOOT,
        {
            uid,
        },
        false
    );
}


async function actionRequest(deviceUid, action) {
    return await ServiceHelper.postRequest( 
        URL_CONSTANTS.DEVICE_ACTION_REQUEST,
        {
            uid: deviceUid,
            action,
        },
    );
}


async function actionConfirm(deviceUid, actionUid, packet) {
    return await ServiceHelper.postRequest(
        URL_CONSTANTS.DEVICE_ACTION_CONFIRM,
        {
            uid: deviceUid,
            action_uid: actionUid,
            packet,
        },
    );
}